import { Box, Typography, Paper, useTheme, CircularProgress, FormControl, Select, MenuItem } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Product } from "../../../db_access/product";
import { getOrgTokens, ORG_PALETTE } from "../../../theme/style/organizationPalette";

const AIInsightCard = ({ type, title, description }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const dotColor = {
    critical: "#f43f5e", // red
    warning: "#f59e0b", // orange
    info: "#10b981",    // green
  }[type] || "#10b981";

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: dotColor, mt: 1, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "left" }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "textSecondary", mt: 0.5, fontSize: "14px", textAlign: "left", lineHeight: 1.6 }}>{description}</Typography>
      </Box>
    </Box>
  );
};

const ProductInsight = () => {
  const { t } = useTranslation();
  const { selectedProductIds, refresh, setRefresh } = useContextProvider();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  
  const [ai_insight, setAI_insight] = useState();
  const [elapsed, setElapsed] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedModel, setSelectedModel] = useState("5.0");

  const timerRef = useRef(null);

  const startTimer = () => {
    setElapsed(0);
    setIsGenerating(true);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Date.now() - start);
    }, 10);
  };

  const endTimer = () => {
    clearInterval(timerRef.current);
    setIsGenerating(false);
  };

  const generateInsight = (generatingMethod) => {
    if (selectedProductIds.length < 1) return;
    startTimer();
    const product_id = selectedProductIds[0];
    generatingMethod(product_id).finally(() => {
      setRefresh((p) => !p);
      endTimer();
    });
  };

  const models = [
    { label: "5.0", action: Product.generate_insight_5_0 },
    { label: "3.5", action: Product.generate_insight_3_5 },
    { label: "Deepseek", action: Product.generate_insight_deepseek },
    { label: "Gemini Flash", action: Product.generate_insight_gemini_flash },
    { label: "Gemini Pro", action: Product.generate_insight_gemini_pro },
    { label: "Gemini Flash Lite", action: Product.generate_insight_gemini_flash_lite },
  ];

  useEffect(() => {
    if (selectedProductIds.length < 1) return;
    Product.get_insight(selectedProductIds[0])
      .then((res) => res.json())
      .then((data) => setAI_insight(data));
  }, [selectedProductIds, refresh]);

  return (
    <Paper
      variant="Standard Container"
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: ORG_PALETTE.radius.card,
        border: "1px solid",
        borderColor: tok.border.card,
        bgcolor: tok.bg.card
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ p: 1, borderRadius: "10px", bgcolor: "#ecfdf5", color: "#10b981", display: "flex" }}>
            <Typography sx={{ fontSize: "20px" }}>💡</Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 500, pb: 1, fontSize: "20px", borderBottom: `1px solid ${tok.border.divider}` }}>
            {t("Intelligence Insights")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "textSecondary", mr: 1 }}>
            {isGenerating ? `Generating... ${(elapsed / 1000).toFixed(1)}s` : elapsed > 0 ? `Generated in ${(elapsed / 1000).toFixed(1)}s` : ""}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "textSecondary", fontWeight: 500, fontSize: "15px" }}>
              {t("Powered by")}
            </Typography>
            <FormControl variant="outlined" size="small">
              <Select
                value={selectedModel || "5.0"}
                onChange={(e) => {
                  const val = e.target.value;
                  const model = models.find(m => m.label === val);
                  if (model) {
                    setSelectedModel(model.label);
                    generateInsight(model.action);
                  }
                }}
                disabled={isGenerating}
                displayEmpty
                sx={{
                  height: 38,
                  minWidth: 160,
                  fontSize: "14px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  bgcolor: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: tok.border.divider,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  }
                }}
              >
                {models.map(model => (
                  <MenuItem key={model.label} value={model.label} sx={{ fontSize: "14px", fontWeight: 500 }}>
                    {model.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box>
        {isGenerating ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, flexDirection: "column", gap: 2 }}>
            <CircularProgress size={32} sx={{ color: "#10b981" }} />
            <Typography variant="body2" color="textSecondary">{t("AI is analyzing the product footprint...")}</Typography>
          </Box>
        ) : (!ai_insight?.insights?.length && !ai_insight?.env_hotspot?.length) ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t("No insights generated yet. Select an AI model above to generate intelligence insights.")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {ai_insight?.insights?.map((insightText, index) => {
              let title = `${t("Insight")} ${index + 1}`;
              let description = insightText;

              let cleanText = insightText.trim();
              const boldMatch = cleanText.match(/^\*\*([^*]+)\*\*(?:\s*[:-]\s*|\s*\n\s*|\s+)(.*)$/s);
              const colonMatch = cleanText.match(/^([^:\n*]+):\s*(.*)$/s);

              if (boldMatch) {
                title = boldMatch[1].trim();
                description = boldMatch[2].trim() || cleanText;
              } else if (colonMatch && colonMatch[1].length < 60) {
                title = colonMatch[1].trim();
                description = colonMatch[2].trim() || cleanText;
              }

              title = title.replace(/\*\*/g, '');
              description = description.replace(/\*\*/g, '');

              if (title.toLowerCase().startsWith("insight ") && title.length < 15) return null;
              
              const insightPrefixRegex = /^Insight\s+\d+\s*\n*/i;
              description = description.replace(insightPrefixRegex, '');

              return (
                <AIInsightCard
                  key={`insight-${index}`}
                  type="info"
                  title={title}
                  description={description}
                />
              );
            })}

            {ai_insight?.env_hotspot?.map((e, index) => (
              <AIInsightCard
                key={`hotspot-${index}`}
                type="warning"
                title={e.category}
                description={`Hotspot: ${e.hotspot} | Recommendation: ${e.recommendation}`}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ProductInsight;
